import React, { useEffect, useRef, useState } from 'react'

import { richTextToPlainText } from 'utils/richText'
import { useAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAnalysisGenerationActions } from 'redux/actions/AnalysisGenerations'
import { useAnalysisTypeGeneration } from 'redux/hooks/AnalysisGenerations'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'

import Hero from 'components_new/molecules/Hero'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import StatusBox from 'components_new/molecules/StatusBox'

import AnalysisTypeSummary from 'components_new/organisms/analysis_types/AnalysisTypeSummary'

import CompletedAnalysis from './CompletedAnalysis'
import RunningAnalysis from './RunningAnalysis'

import {
  canRun,
  describeGenerationOutcome,
  generationErrorDetail
} from 'utils/analysis'

import {
  AnalysisGeneration,
  AnalysisGenerationStatus,
  isCancelledGeneration
} from 'types/AnalysisGeneration'

interface AnalysisDialogProps {
  id: null | string
  onClose: () => void
  open: boolean
}

/**
 * Starts, follows and cancels a run, and shows the outcome of a run that
 * ends while it is open. Once closed, AnalysisCompletedNotifier announces it.
 */
const AnalysisDialog = (props: AnalysisDialogProps) => {
  const { id, onClose, open } = props

  const { analysisType } = useAnalysisType(id)
  const { follow, unfollow } = useAnalysisGenerationActions()
  const { isHomepal } = useAccountSettings()
  const { t } = useTranslation()

  // A run cancelled from this dialog, shown until the dialog closes.
  const [cancelled, setCancelled] = useState<AnalysisGeneration | null>(null)
  // A run followed here that ended, shown until the dialog closes.
  const [outcome, setOutcome] = useState<AnalysisGeneration | null>(null)

  const { running, generation, triggerFailed, cancel, generate } =
    useAnalysisTypeGeneration(analysisType, setCancelled)

  useEffect(() => {
    if (!open || !id) return

    follow(id)

    return () => unfollow(id)
  }, [open, id, follow, unfollow])

  const followedRun = useRef(false)

  useEffect(() => {
    if (open && running) {
      followedRun.current = true
    } else if (followedRun.current && !running) {
      followedRun.current = false

      if (open && generation && !isCancelledGeneration(generation)) {
        setOutcome(generation)
      }
    }
  }, [open, running, generation])

  useEffect(() => {
    if (!open) {
      setCancelled(null)
      setOutcome(null)
    }
  }, [open])

  if (!id || !analysisType) return null

  const handleRun = () => {
    setCancelled(null)
    setOutcome(null)
    generate()
  }

  const runnable = canRun(analysisType)
  const succeeded = outcome?.status === AnalysisGenerationStatus.SUCCEEDED
  const failed = outcome && !succeeded ? outcome : null
  const failedAborted = failed?.status === AnalysisGenerationStatus.ABORTED
  const errorDetail = failed && isHomepal ? generationErrorDetail(failed) : null

  return (
    <SimpleDialog
      actions={
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 0.5
          }}
        >
          {!running && !succeeded ? (
            <Button
              disabled={!runnable}
              fullWidth={true}
              onClick={handleRun}
              startIcon={<Icon name="PlayCircleFilledWhite" />}
            >
              {t('analysis_types', 'run_analysis')}
            </Button>
          ) : null}

          <Button
            fullWidth={true}
            onClick={onClose}
            variant={running ? 'contained' : 'outlined'}
          >
            {running
              ? t('global', 'continue_in_background')
              : t('global', 'close')}
          </Button>

          {running ? (
            <Button
              color="error"
              fullWidth={true}
              onClick={cancel}
              variant="outlined"
            >
              {t('analysis_types', 'cancel_run')}
            </Button>
          ) : null}
        </Box>
      }
      titleHero={
        running || succeeded ? undefined : (
          <Hero
            background={true}
            body={richTextToPlainText(analysisType.description)}
            icon={{ name: cancelled || failed ? 'AI' : 'AnalysisType' }}
            size="medium"
            title={analysisType.name}
          />
        )
      }
      maxWidth="sm"
      onClose={onClose}
      open={open}
    >
      {running ? (
        <RunningAnalysis id={id} startedAt={generation?.created_at ?? null} />
      ) : succeeded && outcome ? (
        <CompletedAnalysis onGoTo={onClose} result={outcome} />
      ) : (
        <>
          {cancelled ? (
            <StatusBox
              severity="warning"
              sx={{ mb: 2, width: '100%' }}
              title={t('analysis_types', 'run_aborted_title')}
            >
              {describeGenerationOutcome(t, cancelled)}
            </StatusBox>
          ) : null}
          {failed ? (
            <StatusBox
              severity={failedAborted ? 'warning' : 'error'}
              sx={{ mb: 2, width: '100%' }}
              title={
                failedAborted
                  ? t('analysis_types', 'run_aborted_title')
                  : t('analysis_types', 'run_failed_title')
              }
            >
              {describeGenerationOutcome(t, failed)}
              {errorDetail ? (
                <>
                  <br />
                  {errorDetail}
                </>
              ) : null}
            </StatusBox>
          ) : null}
          {triggerFailed ? (
            <StatusBox
              severity="error"
              sx={{ mb: 2, width: '100%' }}
              title={t('analysis_types', 'run_failed_title')}
            >
              {t('analysis_types', 'run_failed_body')}
            </StatusBox>
          ) : null}
          <AnalysisTypeSummary context="dialog" id={id} />
        </>
      )}
    </SimpleDialog>
  )
}

export default AnalysisDialog
